import type { LinksFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import { ClerkProvider } from "@clerk/react-router";
import { Header } from "./components/ui/Header";
import { Footer } from "./components/ui/Footer";
import "./tailwind.css";
import { Route } from "./+types/root";
import * as Sentry from "@sentry/react-router";
import { ErrorBoundary as ErrorBoundaryComponent } from "./components/ErrorBoundary";
import type { ReactNode } from "react";
import { validateIncidentIoConfigurationOnce } from "./utils/incidentio/config-validation";
import { SERVICE_DESK_FALLBACK_EMAIL } from "./lib/constants";

export const links: LinksFunction = () => [];

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()];

export async function loader(args: Route.LoaderArgs) {
  await validateIncidentIoConfigurationOnce();
  return rootAuthLoader(args);
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="bg-white text-[#431908] antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  // Keep SSR/client output deterministic to avoid hydration mismatches.
  const domain = import.meta.env.DEV ? undefined : "servicedesk.rb2.nl";

  return (
    <ClerkProvider
      loaderData={loaderData}
      signUpFallbackRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      domain={domain}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#fe150a] focus:text-white focus:rounded-[12px]"
      >
        Skip to main content
      </a>
      <div className="relative flex min-h-screen flex-col">
        <Header />

        <div className="brand-glass-strip px-4 py-3">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 text-sm text-[#431908]">
            <span className="brand-chip rounded-[999px] border-[#f6c9bc] bg-[#fff3ee] text-[11px] uppercase tracking-[0.12em]">
              fallback
            </span>
            Service Desk unavailable? Please email{" "}
            <a
              href={`mailto:${SERVICE_DESK_FALLBACK_EMAIL}`}
              className="font-semibold text-[#fe150a] underline underline-offset-2"
            >
              {SERVICE_DESK_FALLBACK_EMAIL}
            </a>
            .
          </div>
        </div>
        <main id="main-content" className="grow">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
        <Footer />
      </div>
    </ClerkProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Capture exception in Sentry for production monitoring
  if (error instanceof Error) {
    Sentry.captureException(error);
  }

  // Use the new error boundary component
  return <ErrorBoundaryComponent />;
}
